import { combineReducers } from 'redux';
import { reducer as formReducer } from 'redux-form';
import paginate from './common/reducers/paginate';
import filter from './common/reducers/filter';
import auth from './auth/reducer';
import language from './languages/reducer';
import { lookups, franchisor } from './init/reducers';
import parent from './dashboard/reducer';
import booking from './dashboard/booking/reducer';
import paymentProcessors from './dashboard/booking/paymentMethods/reducer';
import formsAnalytics from './dashboard/individualConsultationFormsAnalytics/reducer';
import * as AuthActionTypes from './auth/actions';
import * as ChildLessonActionTypes from './dashboard/children/actions';
import * as PaymentActionTypes from './dashboard/payments/actions';
import * as EnrolmentActionTypes from './dashboard/enrolments/actions';
import * as IndividualConsultationActionTypes from './dashboard/individualConsultations/actions';
import * as CommonActionTypes from './dashboard/common/actions';
import * as WorkshopEnrolmentActionTypes from './dashboard/workshopEnrolments/actions';
import lodash from 'lodash';
import mergeWith from 'lodash/mergeWith';
import warningMessages from './dashboard/warningMessage/reducer';

const initialGlobalEntitiesState = {
	centers: {}, individualConsultationSchedule: {}, weeklyProgram: {}, lessons: {}, grades: {}, terms: {}, workshops: {}, workshopSessions: {},
	makeupSummary: {}
};

const initialParentSpecificEntitiesState = {
	payments: {}, enrolments: {}, individualConsultations: {}, workshopEnrolments: {},
	individualConsultationsDocuments: {},
	childLessons: {}, weeklyInfos: {},
	individualConsultationsDashboard: {}, workshopSessionsDashboard: {},
	selfAssessments: {}, medicalInformations: {}
};

const entities = (state = { ...initialGlobalEntitiesState, ...initialParentSpecificEntitiesState }, action) => {
	switch (action.type) {
		case AuthActionTypes.LOGOUT:
			// reset parent owned entities at logout
			return { ...state, ...initialParentSpecificEntitiesState };
		default:
			if (action.response && action.response.entities) {
				return mergeWith({}, state, action.response.entities, (obj, src) => {
					if (lodash.isArray(obj)) {
						return src;
					}
				});
			}
			return state;
	}
};

// Updates the pagination data for different actions.
const pagination = combineReducers({
	payments: paginate({
		mapActionToKey: action => 'all',
		types: [
			PaymentActionTypes.PAYMENTS_REQUEST,
			PaymentActionTypes.PAYMENTS_SUCCESS,
			PaymentActionTypes.PAYMENTS_FAILURE
		]
	}),
	enrolments: paginate({
		mapActionToKey: action => 'all',
		types: [
			EnrolmentActionTypes.ENROLMENTS_REQUEST,
			EnrolmentActionTypes.ENROLMENTS_SUCCESS,
			EnrolmentActionTypes.ENROLMENTS_FAILURE
		]
	}),
	individualConsultations: paginate({
		mapActionToKey: action => 'all',
		types: [
			IndividualConsultationActionTypes.INDIVIDUAL_CONSULTATIONS_REQUEST,
			IndividualConsultationActionTypes.INDIVIDUAL_CONSULTATIONS_SUCCESS,
			IndividualConsultationActionTypes.INDIVIDUAL_CONSULTATIONS_FAILURE
		]
	}),
	individualConsultationDocumentsByChildAndType: paginate({
		mapActionToKey: action => `${action.childId}-${action.documentType}`,
		types: [
			IndividualConsultationActionTypes.INDIVIDUAL_CONSULTATION_DOCUMENTS_REQUEST,
			IndividualConsultationActionTypes.INDIVIDUAL_CONSULTATION_DOCUMENTS_SUCCESS,
			IndividualConsultationActionTypes.INDIVIDUAL_CONSULTATION_DOCUMENTS_FAILURE
		]
	}),
	workshopEnrolments: paginate({
		mapActionToKey: action => 'all',
		types: [
			WorkshopEnrolmentActionTypes.WORKSHOP_ENROLMENTS_REQUEST,
			WorkshopEnrolmentActionTypes.WORKSHOP_ENROLMENTS_SUCCESS,
			WorkshopEnrolmentActionTypes.WORKSHOP_ENROLMENTS_FAILURE
		]
	}),
});

// Updates the filtered data for different actions.
const filters = combineReducers({
	childLessonsByChildAndProgram: filter({
		mapActionToKey: action => `${action.childId}-${action.programId}`,
		types: [
			ChildLessonActionTypes.CHILD_LESSONS_REQUEST,
			ChildLessonActionTypes.CHILD_LESSONS_SUCCESS,
			ChildLessonActionTypes.CHILD_LESSONS_FAILURE
		]
	}),
	childLessonsForDashboard: filter({
		mapActionToKey: action => 'all',
		types: [
			ChildLessonActionTypes.CHILD_LESSONS_DASHBOARD_REQUEST,
			ChildLessonActionTypes.CHILD_LESSONS_DASHBOARD_SUCCESS,
			ChildLessonActionTypes.CHILD_LESSONS_DASHBOARD_FAILURE
		]
	}),
	childConsultationsForDashboard: filter({
		mapActionToKey: action => 'all',
		types: [
			IndividualConsultationActionTypes.INDIVIDUAL_CONSULTATION_DASHBOARD_REQUEST,
			IndividualConsultationActionTypes.INDIVIDUAL_CONSULTATION_DASHBOARD_SUCCESS,
			IndividualConsultationActionTypes.INDIVIDUAL_CONSULTATION_DASHBOARD_FAILURE
		]
	}),
	individualConsultationScheduleByChildAndCenter: filter({
		mapActionToKey: action => `${action.childId || 'all'}-${action.centerId}`,
		types: [
			CommonActionTypes.INDIVIDUAL_CONSULTATION_SCHEDULE_REQUEST,
			CommonActionTypes.INDIVIDUAL_CONSULTATION_SCHEDULE_SUCCESS,
			CommonActionTypes.INDIVIDUAL_CONSULTATION_SCHEDULE_FAILURE
		]
	}),
	workshopsByCenter: filter({
		mapActionToKey: action => `${action.centerId}`,
		types: [
			CommonActionTypes.WORKSHOPS_REQUEST,
			CommonActionTypes.WORKSHOPS_SUCCESS,
			CommonActionTypes.WORKSHOPS_FAILURE
		]
	}),
	workshopSessions: filter({
		mapActionToKey: action => `${action.workshopId}`,
		types: [
			CommonActionTypes.WORKSHOP_SESSIONS_REQUEST,
			CommonActionTypes.WORKSHOP_SESSIONS_SUCCESS,
			CommonActionTypes.WORKSHOP_SESSIONS_FAILURE
		]
	}),
	childWorkshopSessionsForDashboard: filter({
		mapActionToKey: action => 'all',
		types: [
			WorkshopEnrolmentActionTypes.CHILD_WORKSHOP_SESSIONS_DASHBOARD_REQUEST,
			WorkshopEnrolmentActionTypes.CHILD_WORKSHOP_SESSIONS_DASHBOARD_SUCCESS,
			WorkshopEnrolmentActionTypes.CHILD_WORKSHOP_SESSIONS_DASHBOARD_FAILURE
		]
	}),
	makeupSummaryByChildAndTerm: filter({
		mapActionToKey: action => `${action.childId}-${action.termId}`,
		types: [
			ChildLessonActionTypes.MAKEUP_SUMMARY_BY_CHILD_AND_TERM_REQUEST,
			ChildLessonActionTypes.MAKEUP_SUMMARY_BY_CHILD_AND_TERM_SUCCESS,
			ChildLessonActionTypes.MAKEUP_SUMMARY_BY_CHILD_AND_TERM_FAILURE
		]
	})
});

const rootReducer = combineReducers({
	lookups,
	entities,
	pagination,
	filters,
	form: formReducer,
	formsAnalytics,
	auth,
	parent,
	booking,
	paymentProcessors,
	configuration: combineReducers({ franchisor, language, warningMessages })
});

export default rootReducer;